import { geolocationRefusal, type GeolocationRefusal, type GeoPoint } from '@bms2/shared';

/**
 * One read of the browser's own location (decisions 0674, 0675 and 3440). The
 * browser shows its own consent prompt the first time a site asks, and a refusal
 * comes back named rather than as a blank: permission denied, an address that is
 * not secure, the position unavailable, the reading timed out, or no reader at
 * all. 🔨 Nothing is written from here; the caller decides what a point or a
 * refusal means.
 *
 * 🔒 The read starts inside the press that asks for it and nowhere else (decision
 * 3440): `getCurrentPosition` is called before this function awaits anything, so
 * a caller that calls it first thing in a tap handler asks while the tap still
 * counts as the person's own gesture. Safari on an iPhone prompts only then.
 */
export type PositionAnswer = { ok: true; point: GeoPoint } | { ok: false; refusal: GeolocationRefusal };

/** How long a reading is given before it counts as timed out. */
export const POSITION_TIMEOUT_MS = 15_000;

/** The reader the page is given; the browser's own where there is one, so a suite can hand in a stub. */
export type GeolocationReader = Pick<Geolocation, 'getCurrentPosition'>;

export function browserGeolocation(): GeolocationReader | null {
  return typeof navigator !== 'undefined' && navigator.geolocation ? navigator.geolocation : null;
}

/**
 * Whether the page was opened at an address the browser counts as secure. 🔒 A
 * browser reads its location over HTTPS or on the machine's own address alone, and
 * refuses every other address with the same code a refused permission carries, so
 * the page asks first and names the address rather than the permission (decision 3440).
 */
export function pageIsSecure(): boolean {
  return typeof window === 'undefined' || window.isSecureContext !== false;
}

export function readPosition(reader: GeolocationReader | null = browserGeolocation(), timeoutMs = POSITION_TIMEOUT_MS, secure = pageIsSecure()): Promise<PositionAnswer> {
  if (!secure) return Promise.resolve({ ok: false, refusal: 'insecure' });
  if (!reader) return Promise.resolve({ ok: false, refusal: 'unsupported' });
  return new Promise((resolve) => {
    reader.getCurrentPosition(
      (position) => resolve({ ok: true, point: { latitude: position.coords.latitude, longitude: position.coords.longitude } }),
      (error) => resolve({ ok: false, refusal: geolocationRefusal(error?.code) }),
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 0 },
    );
  });
}
